import * as React from 'react';
import axios from 'axios';
import LogPlusAnyResourcesAutocomplete from './LogPlusAnyResourcesAutocomplete';
import { AutocompleteMode, IAgreementListItem, IRelatedAgreementListItem, parseToAgreement, parseFromAgreement } from './IModels';
import { Typography } from '@material-ui/core';

interface GetAgreementsAutocompleteResult {
	success: boolean,
	status?: "LogDisabled",
	items?: IAgreementListItem[]
}

export interface IAutocompleteParams {
	maxRows: number,
	filterId?: string,
}

interface IProps<T> {
	value: T[],
	onSelect: (value: T[]) => void,
	multiple?: true | false,
	placeholderText?: string,
	error: boolean;
	helperText: string,
	searchIn: string,
	mode: AutocompleteMode,
	notifier: { value: string, label: string },
	filterId: string;
	relatedFilterId: string;
}

export default function LogPlusAgreementsAutocomplete(props: IProps<IAgreementListItem>) {

    function parseRelated() {
		return props.value.map(parseFromAgreement);
    }

    function parseFromRelated(r: IRelatedAgreementListItem[]) {
        return r.map(parseToAgreement);
    }

    switch (props.mode) {
        case 'relatedUser':
        case 'relatedNotifier':
			return (<RelatedAgreementsAutocomplete
                searchIn={props.searchIn?.split(',') as (keyof IRelatedAgreementListItem)[]}
                notifierId={props.notifier?.value}
                mode={props.mode}
                error={props.error}
                helperText={props.helperText}
                multiple={props.multiple}
				value={parseRelated()}
				filterId={props.relatedFilterId}
                onSelect={(r) => {
                    let data = parseFromRelated(r);
                    props.onSelect(data);
                }}
            />);
        default:
            return (<AgreementAutocomplete
				value={props.value}
				onSelect={props.onSelect}

				searchIn={props.searchIn?.split(',') as (keyof IAgreementListItem)[]}
				error={props.error}
				helperText={props.helperText}
				multiple={props.multiple}
				filterId={props.filterId}
            />);
    }
}

interface IAgreementAutocompleteProps {
    value: IAgreementListItem[],
    onSelect: (value: IAgreementListItem[]) => void,
    multiple?: true | false,
    placeholderText?: string,
    error?: boolean;
    helperText?: string,

	searchIn: (keyof IAgreementListItem)[],
	filterId: string;
}

function AgreementAutocomplete(props: IAgreementAutocompleteProps)
{
	const getOptionSelected = (option: IAgreementListItem, value: IAgreementListItem) => {
		return option.Id === value.Id;
	}

	return (<LogPlusAnyResourcesAutocomplete
		{...props}
		getOptionLabel={(option: IAgreementListItem) => option.Name}
		getOptionSelected={getOptionSelected}
		createRequest={(text: string) => {
			return {
				text: text,
			}
		}}
		loadOptions={(request, callback) => {

			axios.get<GetAgreementsAutocompleteResult>('/LogPlusResources/GetAgreementsAutocomplete/', {
				params: {
					search: request.text,
					searchIn: props?.searchIn?.join(','),
					max: 20,
					filterId: props.filterId,
				}
			})
				.then(r => {
					callback(r.data.items);
				});
		}}
		renderOption={(option) => {
			return (<div>
				{option.Name}
				<Typography variant="body2" color="textSecondary">
					{option.Description}
				</Typography>
			</div>);
		}}
	/>)
}

export interface GetRelatedAgreementsAutocompleteResourcesResult {
	success: boolean,
	status?: "LogDisabled",
	items?: IRelatedAgreementListItem[]
}

interface IRelatedAgreementsAutocompleteProps {
	value: IRelatedAgreementListItem[],
	onSelect: (value: IRelatedAgreementListItem[]) => void,
	multiple?: true | false,
	placeholderText?: string,
	error?: boolean;
	helperText?: string,

	notifierId: string | null,
	searchIn: (keyof IRelatedAgreementListItem)[],
	mode: AutocompleteMode,
	filterId: string,
}

function RelatedAgreementsAutocomplete(props: IRelatedAgreementsAutocompleteProps) {
	
	let notifierId: string = null
	if (props.mode === 'relatedNotifier') {
		notifierId = props.notifierId;
	}

	return (<LogPlusAnyResourcesAutocomplete
		{...props}
		getOptionLabel={(option: IRelatedAgreementListItem) => option.Name}
		getOptionSelected={(o, v) => o.Id === v.Id}
		cachePrefix={notifierId}
		createRequest={(text: string) => {
			return {
				text: text,
				notifierId: notifierId,
			}
		}}
		loadOptions={(request, callback) => {

			if (!request.notifierId && props.mode === 'relatedNotifier') {
				callback([]);
			}

			axios.get<GetRelatedAgreementsAutocompleteResourcesResult>('/LogPlusResources/GetRelatedAgreementsAutocomplete/', {
				params: {
					notifierId: request.notifierId,
					search: request.text,
					searchIn: props.searchIn?.join(','),
					max: 20,
					filterId: props.filterId,
				}
			})
				.then(r => {
					callback(r.data.items);
				});
		}}
		renderOption={(option) => {
			return (<div>
				{option.Name}
				<Typography variant="body2" color="textSecondary">
					{option.Description}
				</Typography>
			</div>);
		}}
	/>);
}